/**
 * Historico de versoes de um modelo, da mais nova para a mais antiga.
 *
 * Cada versao mostra quando, quem, o que mudou e o status daquele momento
 * (inclusive a recusa da Meta, quando houve). "Ver como era" abre a previa
 * daquela versao ali mesmo; "Restaurar" cria uma versao nova com aquele
 * conteudo (versoes.ts) -- nunca apaga as do meio.
 *
 * Restaurar mostra antes, em uma linha, o que vai mudar em relacao ao atual:
 * restaurar as cegas um texto de dois meses atras e como a regua muda sem
 * ninguem perceber.
 *
 * O historico cresce a cada salvar: mostra as versoes mais recentes e abre o
 * resto sob pedido, para um modelo editado cem vezes nao virar um modal
 * de cem cartoes.
 */

import { useState } from 'react';
import { Badge, Button, Icon } from '../../../components/ui';
import { formatDateTime } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { MessageTemplate, TemplateVersion } from '../types';
import { STATUS_TOM, canalPassaPelaMeta, statusLabel } from '../vocabulario';
import { frasearMudancas, resumirMudancas } from '../versoes';
import { PreviaDoCanal } from './PreviaDoCanal';

const VERSOES_VISIVEIS = 8;

function comoEra(t: MessageTemplate, v: TemplateVersion): MessageTemplate {
  return { ...t, ...v.content, status: v.status, statusNote: v.statusNote };
}

export function HistoricoVersoes({
  template,
  empresa,
  onRestaurar,
}: {
  readonly template: MessageTemplate;
  readonly empresa: CompanySummary | null;
  readonly onRestaurar: (versao: TemplateVersion) => void;
}): JSX.Element {
  const [aberta, setAberta] = useState<number | null>(null);
  const [todas, setTodas] = useState(false);
  const versoes = [...template.versions].reverse();
  const mostradas = todas ? versoes : versoes.slice(0, VERSOES_VISIVEIS);
  const escondidas = versoes.length - mostradas.length;
  const atual = template.versions[template.versions.length - 1];

  if (versoes.length === 0) {
    return <p className="text-sm text-ink-subtle">Sem histórico ainda. Cada vez que o modelo for salvo, uma versão nova aparece aqui.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ink-subtle">
        {versoes.length === 1 ? '1 versão' : `${versoes.length} versões`}. Restaurar cria uma versão nova com o conteúdo escolhido
        {canalPassaPelaMeta(template.channel) ? ' e volta para a análise da Meta' : ''}; nenhuma versão é apagada.
      </p>
      <ol className="flex flex-col gap-2">
        {mostradas.map((v) => {
          const ehAtual = atual !== undefined && v.version === atual.version;
          const anterior = comoEra(template, v);
          const mudancas = ehAtual ? [] : resumirMudancas(template, anterior);
          const expandida = aberta === v.version;
          return (
            <li key={v.version} className={`rounded-xl border px-4 py-3 ${ehAtual ? 'border-brand/40 bg-brand/5' : 'border-line bg-surface'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                    <span className="font-mono">v{v.version}</span>
                    {ehAtual ? (
                      <Badge tone="brand" dot>
                        Atual
                      </Badge>
                    ) : null}
                    <Badge tone={STATUS_TOM[v.status]}>{statusLabel(v.status, template.channel)}</Badge>
                  </p>
                  <p className="mt-0.5 text-sm text-ink">{v.summary}</p>
                  <p className="text-xs text-ink-subtle">
                    {formatDateTime(v.savedAt)} · {v.savedBy}
                  </p>
                  {v.statusNote === null ? null : <p className="mt-1 text-xs text-ink-muted">{v.statusNote}</p>}
                  {ehAtual ? null : (
                    <p className="mt-1 text-xs text-ink-muted">
                      <Icon name="info" size="xs" className="mr-1 inline" />
                      Restaurar muda: {mudancas.length === 0 ? 'nada (conteúdo igual ao atual)' : frasearMudancas(mudancas).toLowerCase()}.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="ghost" size="sm" icon="olho" aria-expanded={expandida} onClick={() => setAberta(expandida ? null : v.version)}>
                    {expandida ? 'Esconder' : 'Ver como era'}
                  </Button>
                  {ehAtual ? null : (
                    <Button variant="secondary" size="sm" icon="atualizar" onClick={() => onRestaurar(v)} disabled={mudancas.length === 0}>
                      Restaurar
                    </Button>
                  )}
                </div>
              </div>
              {expandida ? (
                <div className="mt-3">
                  <PreviaDoCanal template={anterior} empresa={empresa} compacto />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {escondidas > 0 || todas ? (
        <div>
          <Button variant="ghost" size="sm" icon="chevron" aria-expanded={todas} onClick={() => setTodas((v) => !v)}>
            {todas ? 'Mostrar só as mais recentes' : `Ver ${escondidas === 1 ? 'a versão anterior' : `as ${escondidas} versões anteriores`}`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
